import React, { useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { compose, withApollo } from 'react-apollo';
import cn from 'classnames';
import { MainNavContextProvider } from 'chameleon/ui-stack/ui/basic/main-nav-two-levels';
import { BreadcrumbProvider } from 'chameleon/ui-stack/ui/basic/breadcrumbs';
import useFetchFlags from 'chameleon/ui-stack/hooks/use-fetch-flags';
import useFetchFlagsWithConfig from 'chameleon/ui-stack/hooks/use-fetch-flags-with-config';
import setFeatureFlag from 'legacy-src/actions/featureFlags';
import useStudentAppcues from 'chameleon/ui-stack/hooks/use-student-app-cues';
import useFetchUserInfo from 'chameleon/ui-stack/hooks/use-fetch-user-info';
import useAppCues, {
  useHelpMenuPin,
} from 'chameleon/ui-stack/hooks/use-app-cues';
import Notifications from 'chameleon/ui-stack/utilities/notifications';
import { PREAP_SUBJECTS } from 'chameleon-src/constants/subjects';
import ImpersonateHeader from 'legacy-src/components/impersonating/components/ImpersonateHeader';
import { isImpersonating } from 'legacy-src/components/impersonating/utils';
import ContactUs from 'chameleon-src/components/ContactUs';
import ContactUsPreap from 'chameleon-src/components/ContactUsPreap';

import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import StudentCBAlert from 'chameleon-src/components/StudentCBAlert';

import 'react-datepicker/dist/react-datepicker.css';
import { TeacherOrganizeProvider } from 'legacy-src/components/TeacherOrganize/hooks/useTeacherOrganize';
import { TeacherAssignmentsProvider } from 'legacy-src/components/TeacherAssignments/hooks/useTeacherAssignments';
import {
  overrideMathJaxConfiguration,
  preloadMathJaxWithLearnosity,
} from 'chameleon/src/utility/utils';
import useMaintenancePage from 'chameleon/ui-stack/hooks/use-maintenance-page';
import {
  Context as ChameleonContext,
  TYPES,
} from 'chameleon/ui-stack/utilities/app-loader';

/**
 * @todo - legacy functionalities
 * - Tracker
 * - SessionExpiringModal - connect wit Ryan
 * - impersonating functionality?
 * */
const App = (props) => {
  const {
    children,
    userData,
    setFeatureFlagAction,
    showAppcues,
    client,
    dispatch,
    router,
    supportLiveChat,
    enableAppcuesStudentSpp,
    subjectData,
    selfHostMathJax,
    studentAlertConfig,
    enableStudentAppcuesFlag,
  } = props;
  const { isHomePage, location, params } = useNavigation({
    router,
  });

  const [, chameleonDispatch] = useContext(ChameleonContext);
  const { pathname, query, search } = location;
  const { subject, splat = '' } = params;
  const masterSubjectId = subject || splat.split('/')[0] || '';
  const { me, isTeacher, isStudent } = userData;
  const { initId, schoolSet } = me;

  const schoolDistrictIds = (schoolSet || []).map((school) => school.parentId);
  const schoolIds = (schoolSet || []).map((school) => school.initId);

  const showContactUs = query.contactus === 'show';

  const showStudentAlert =
    studentAlertConfig?.treatment && isHomePage && isStudent;

  const [userInfo, isTeacherInfoLoading] = useFetchUserInfo({
    client,
    dispatch,
    userData,
    subject: masterSubjectId,
    router,
    params,
  });

  const studentLabelsForCurrentSubject =
    userInfo?.data?.studentSubject?.labels || [];

  const teacherSubjects = userInfo?.data?.teacherSubjects;
  const isPreApSubject = Boolean(
    PREAP_SUBJECTS.find((s) => s === Number.parseInt(masterSubjectId, 10)),
  );

  useFetchFlagsWithConfig({
    initId: initId || -1,
    setFeatureFlagAction,
    attributes: {
      isTeacher,
      districts: schoolDistrictIds,
      masterSubjectId,
      schoolIds,
    },
  });

  useFetchFlags({
    initId: initId || -1,
    setFeatureFlagAction,
    attributes: {
      isTeacher,
      districts: schoolDistrictIds,
      masterSubjectId,
      schoolIds,
    },
  });

  useStudentAppcues({
    pathname,
    setFeatureFlagAction,
    isStudent,
    search,
    userId: initId || -1,
  });

  useMaintenancePage({
    flagTrafficKeys: schoolIds,
    setFeatureFlagAction,
  });

  useHelpMenuPin(router);
  useAppCues({
    showAppcues,
    userData,
    pathname,
    query,
    enableAppcuesStudentSpp,
    enableStudentAppcuesFlag,
    masterSubjectId,
  });

  useEffect(() => {
    // Start the Salesforce chat if the feature flag is on
    if (supportLiveChat) {
      window._satellite.track('tags.embed.prog.liveAgent.apclassroom');
    }
  }, [supportLiveChat]);

  useEffect(() => {
    overrideMathJaxConfiguration();
    if (!isPreApSubject && selfHostMathJax) {
      preloadMathJaxWithLearnosity();
    }
  }, [selfHostMathJax, isPreApSubject]);

  useEffect(() => {
    if (isPreApSubject) {
      chameleonDispatch({
        type: TYPES.SET_USE_CHAMELEON,
        payload: {
          isPreApSubject: true,
          preApChameleonFlag: false,
        },
      });
    }
  }, [isPreApSubject]);

  const _isImpersonating = isImpersonating();

  return (
    <div>
      {showContactUs &&
        (isPreApSubject ? (
          <ContactUsPreap userData={userData} subjectData={subjectData} />
        ) : (
          <ContactUs userData={userData} subjectData={subjectData} />
        ))}
      {showStudentAlert && (
        <StudentCBAlert uiConfig={JSON.parse(studentAlertConfig?.config)} />
      )}
      {_isImpersonating && <ImpersonateHeader />}
      <div
        className={cn('flex h-full flex-col w-full', {
          'mt-20.2': _isImpersonating,
        })}
      >
        <Notifications />
        <TeacherOrganizeProvider
          subjects={
            isStudent
              ? [
                  {
                    id: subject,
                    labels: studentLabelsForCurrentSubject,
                  },
                ]
              : teacherSubjects
          }
          router={router}
          isTeacherInfoLoading={isTeacherInfoLoading}
        >
          <TeacherAssignmentsProvider>
            <MainNavContextProvider>
              <BreadcrumbProvider params={params}>
                {children}
              </BreadcrumbProvider>
            </MainNavContextProvider>
          </TeacherAssignmentsProvider>
        </TeacherOrganizeProvider>
      </div>
    </div>
  );
};

const mapStateToProps = ({
  userData,
  featureFlag,
  loadedReadyToScoreBySubject,
  subject,
}) => ({
  userData,
  showAppcues: featureFlag.show_appcues_in_app_help,
  studentAlertConfig: featureFlag.student_cb_alert,
  supportLiveChat: featureFlag.support_live_chat,
  loadedReadyToScoreBySubject,
  enableAppcuesStudentSpp: featureFlag.enable_appcues_student_spp,
  selfHostMathJax: featureFlag.use_self_hosted_mathjax,
  subjectData: subject.data,
  enableStudentAppcuesFlag: featureFlag.set_student_app_cues,
});

export default compose(
  withApollo,
  connect(mapStateToProps, (dispatch) => ({
    dispatch,
    setFeatureFlagAction: (...args) => dispatch(setFeatureFlag(...args)),
  })),
)(App);
